import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  INK,
  BLUE,
  BLUE_SOFT,
  GREEN,
  RED,
  AMBER,
  CANVAS,
  LINE,
  MUTED,
  CARD_SHADOW,
  DAYS_SHORT,
  DAYS_FULL,
} from './tokens';
import {
  pad,
  formatTime,
  formatDuration,
  computeEnd,
  crossesMidnight,
  midnightPercent,
} from './bonusSchedule';

/* ---------- Confirm dialog ---------- */

const DANGER = '#D92D53';

export function ConfirmDialog({
  open,
  title,
  message,
  highlight,
  confirmLabel = 'Delete',
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  return (
    <div
      className="nds-bonus"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(11,19,84,0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 24,
          padding: 28,
          boxShadow: '0 24px 60px rgba(11,19,84,0.35)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(217,45,83,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <DeleteOutlineIcon style={{ color: DANGER, fontSize: 28 }} />
        </div>
        <h2
          style={{
            fontSize: 19,
            fontWeight: 800,
            color: INK,
            margin: '0 0 6px',
          }}
        >
          {title}
        </h2>
        {highlight && (
          <p
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: INK,
              margin: '0 0 8px',
            }}
          >
            {highlight}
          </p>
        )}
        <p
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: MUTED,
            margin: '0 0 22px',
            lineHeight: 1.5,
          }}
        >
          {message}
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '13px 0',
              borderRadius: 14,
              border: `1px solid ${LINE}`,
              cursor: 'pointer',
              background: '#fff',
              color: INK,
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: '13px 0',
              borderRadius: 14,
              border: 'none',
              cursor: 'pointer',
              background: DANGER,
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              boxShadow: '0 8px 18px rgba(217,45,83,0.3)',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Card + ghost button ---------- */

export function Card({ title, action, children }) {
  return (
    <div
      style={{
        borderRadius: 24,
        background: '#fff',
        boxShadow: CARD_SHADOW,
        padding: '20px 18px',
        marginBottom: 16,
      }}
    >
      {(title || action) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            padding: '0 4px',
          }}
        >
          {title && (
            <h2
              style={{
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                margin: 0,
                color: INK,
              }}
            >
              {title}
            </h2>
          )}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function GhostButton({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        marginTop: 12,
        width: '100%',
        padding: '12px 0',
        borderRadius: 16,
        border: 'none',
        cursor: 'pointer',
        background: BLUE_SOFT,
        color: BLUE,
        fontSize: 14,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}
    >
      <AddIcon style={{ fontSize: 18 }} />
      {children}
    </button>
  );
}

/* ---------- Switch (spec §9.2) ---------- */

export function Switch({ on, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={disabled ? undefined : onChange}
      style={{
        position: 'relative',
        width: 52,
        height: 31,
        borderRadius: 999,
        border: 'none',
        flexShrink: 0,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        background: on ? GREEN : '#D5D8E6',
        transition: 'background 200ms',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: 2,
          width: 27,
          height: 27,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 2px 4px rgba(11,19,84,0.2)',
          transform: `translateX(${on ? 21 : 0}px)`,
          transition: 'transform 200ms',
        }}
      />
    </button>
  );
}

/* ---------- Status selector (Active / Inactive) ---------- */

export function StatusSelector({ active, onChange }) {
  const options = [
    { label: 'Active', value: true, color: GREEN },
    { label: 'Inactive', value: false, color: RED },
  ];
  return (
    <div
      style={{
        display: 'flex',
        borderRadius: 16,
        padding: 4,
        background: CANVAS,
      }}
    >
      {options.map((o) => {
        const sel = active === o.value;
        return (
          <button
            type="button"
            key={o.label}
            onClick={() => onChange(o.value)}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: 12,
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: sel ? o.color : 'transparent',
              color: sel ? '#fff' : MUTED,
              transition: 'background 150ms',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: sel ? '#fff' : '#C9CDDE',
              }}
            />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Day chips (Monday first) ---------- */

export function DayChips({ days, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {DAYS_SHORT.map((d, i) => {
        const on = days.includes(i);
        return (
          <button
            type="button"
            key={i}
            aria-label={DAYS_FULL[i]}
            onClick={() => onToggle(i)}
            style={{
              flex: 1,
              minWidth: 0,
              height: 40,
              maxWidth: 56,
              borderRadius: 999,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 700,
              background: on ? INK : '#fff',
              color: on ? '#fff' : MUTED,
              border: `1px solid ${on ? INK : LINE}`,
              transition: 'background 120ms',
            }}
          >
            {d}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Pill dropdown ---------- */

function Pill({ value, onChange, options }) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none',
          padding: '10px 30px 10px 14px',
          borderRadius: 12,
          fontSize: 17,
          fontWeight: 700,
          background: '#fff',
          color: INK,
          border: `1px solid ${LINE}`,
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        {options}
      </select>
      <KeyboardArrowDownIcon
        style={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          fontSize: 16,
          color: MUTED,
        }}
      />
    </div>
  );
}

/* ---------- 12-hour time picker (spec §9.2 <TimePicker12>) ---------- */

export function TimePicker12({ startMin, onChange }) {
  const startH = Math.floor(startMin / 60);
  const startM = startMin % 60;
  const isPM = startH >= 12;
  const displayHour = startH % 12 === 0 ? 12 : startH % 12;

  const setStart = (h, m) => onChange(h * 60 + m);
  const onHour = (val) => {
    const v = Number(val);
    setStart(isPM ? (v % 12) + 12 : v % 12, startM);
  };
  const setMeridiem = (pm) =>
    setStart(pm ? (startH % 12) + 12 : startH % 12, startM);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      <Pill
        value={displayHour}
        onChange={onHour}
        options={Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      />
      <span style={{ fontSize: 18, fontWeight: 700, color: MUTED }}>:</span>
      <Pill
        value={startM}
        onChange={(v) => setStart(startH, Number(v))}
        options={[0, 15, 30, 45].map((m) => (
          <option key={m} value={m}>
            {pad(m)}
          </option>
        ))}
      />
      <div
        style={{
          display: 'flex',
          borderRadius: 12,
          overflow: 'hidden',
          border: `1px solid ${LINE}`,
        }}
      >
        {[
          { label: 'AM', pm: false },
          { label: 'PM', pm: true },
        ].map((o) => (
          <button
            type="button"
            key={o.label}
            onClick={() => setMeridiem(o.pm)}
            style={{
              padding: '10px 14px',
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 700,
              background: isPM === o.pm ? INK : '#fff',
              color: isPM === o.pm ? '#fff' : MUTED,
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Duration stepper (spec §9.2 <DurationStepper>) ---------- */

export function DurationStepper({ durationMin, onChange }) {
  const stepBtn = (label, fn) => (
    <button
      type="button"
      onClick={fn}
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        border: 'none',
        cursor: 'pointer',
        background: CANVAS,
        color: INK,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: 12,
          background: '#fff',
          border: `1px solid ${LINE}`,
          padding: 8,
        }}
      >
        {stepBtn(<RemoveIcon style={{ fontSize: 18 }} />, () =>
          onChange(Math.max(5, durationMin - 5))
        )}
        <span
          style={{
            fontSize: 18,
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            color: INK,
          }}
        >
          {formatDuration(durationMin)}
        </span>
        {stepBtn(<AddIcon style={{ fontSize: 18 }} />, () =>
          onChange(Math.min(1440, durationMin + 5))
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        {[
          { label: '15m', min: 15 },
          { label: '30m', min: 30 },
          { label: '45m', min: 45 },
          { label: '1h', min: 60 },
          { label: '2h', min: 120 },
        ].map((preset) => {
          const on = durationMin === preset.min;
          return (
            <button
              type="button"
              key={preset.min}
              onClick={() => onChange(preset.min)}
              style={{
                flex: 1,
                padding: '6px 0',
                borderRadius: 10,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700,
                background: on ? BLUE_SOFT : '#fff',
                color: on ? BLUE : MUTED,
                border: `1px solid ${on ? BLUE_SOFT : LINE}`,
              }}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Schedule result panel (spec §4.4) ---------- */

export function ScheduleResult({ block }) {
  const { days, allDay, startTime, durationMinutes } = block;
  const startMin =
    startTime != null
      ? Number(startTime.split(':')[0]) * 60 + Number(startTime.split(':')[1])
      : 0;

  const box = {
    borderRadius: 16,
    background: '#fff',
    padding: 16,
    border: `1px solid ${LINE}`,
  };

  if (!days || days.length === 0) {
    return (
      <div style={box}>
        <p style={{ fontSize: 13, fontWeight: 500, color: MUTED, margin: 0 }}>
          Pick a day to see when this bonus runs.
        </p>
      </div>
    );
  }

  const eyebrow = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: MUTED,
    margin: 0,
  };

  if (allDay) {
    return (
      <div style={box}>
        <p style={{ ...eyebrow, marginBottom: 10 }}>Runs all day</p>
        <div style={{ display: 'grid', gap: 6 }}>
          {days
            .slice()
            .sort((a, b) => a - b)
            .map((d) => (
              <div
                key={d}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 700, color: INK }}>
                  {DAYS_FULL[d]}
                </span>
                <span style={{ fontWeight: 700, color: GREEN }}>All day</span>
              </div>
            ))}
        </div>
      </div>
    );
  }

  const cross = crossesMidnight(startMin, durationMinutes);
  const tickPct = midnightPercent(startMin, durationMinutes);

  return (
    <div style={box}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 8,
        }}
      >
        <div>
          <p style={eyebrow}>Starts</p>
          <p style={{ fontSize: 15, fontWeight: 800, margin: 0, color: INK }}>
            {formatTime(startMin)}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={eyebrow}>Ends</p>
          <p style={{ fontSize: 15, fontWeight: 800, margin: 0, color: INK }}>
            {formatTime((startMin + durationMinutes) % 1440)}
          </p>
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          height: 8,
          borderRadius: 999,
          background: BLUE_SOFT,
          marginBottom: cross ? 2 : 12,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${BLUE}, ${INK})`,
          }}
        />
        {cross && (
          <div
            style={{
              position: 'absolute',
              top: -4,
              height: 16,
              width: 2,
              borderRadius: 2,
              left: `${tickPct}%`,
              background: AMBER,
            }}
          />
        )}
      </div>
      {cross && (
        <p
          style={{
            ...eyebrow,
            marginBottom: 12,
            marginLeft: `${Math.min(tickPct, 82)}%`,
          }}
        >
          12 AM
        </p>
      )}

      <div style={{ display: 'grid', gap: 6, paddingTop: 2 }}>
        {days
          .slice()
          .sort((a, b) => a - b)
          .map((d) => {
            const { endDay, endMin, nextDay } = computeEnd(
              d,
              startMin,
              durationMinutes
            );
            return (
              <div
                key={d}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 700, color: INK }}>
                  {DAYS_FULL[d]}
                </span>
                <span style={{ fontWeight: 500, color: MUTED }}>
                  {formatTime(startMin)}
                  <span style={{ margin: '0 6px' }}>→</span>
                  <span style={{ color: INK, fontWeight: 700 }}>
                    {nextDay ? `${DAYS_FULL[endDay].slice(0, 3)} ` : ''}
                    {formatTime(endMin)}
                  </span>
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
}
